import { useEffect, useRef } from 'react';

export const useIntersectionObserveWithDelay = (
  ref: React.MutableRefObject<Element | null>,
  callback: () => void,
  options?: IntersectionObserverInit,
  delay = 2000
) => {
  const callbackRef = useRef(callback);
  const timeoutRef = useRef<number | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const hasStartedRef = useRef(false);

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  useEffect(() => {
    if (typeof IntersectionObserver !== 'function') return;

    const handleIntersecting = (entry: IntersectionObserverEntry) => {
      if (hasStartedRef.current) return;

      if (entry.isIntersecting) {
        // Используем nullish coalescing оператор вместо простого присваивания
        timeoutRef.current ??= window.setTimeout(() => {
          callbackRef.current();
          hasStartedRef.current = true;
          observerRef.current?.unobserve(entry.target);
        }, delay);
      } else {
        // Элемент скрылся — сбрасываем таймер, но не флаг
        clearTimeout(timeoutRef.current ?? undefined);
        timeoutRef.current = null;
      }
    };

    const handler = (entries: IntersectionObserverEntry[]) => {
      for (const entry of entries) {
        handleIntersecting(entry);
      }
    };

    // Очищаем предыдущий observer
    observerRef.current?.disconnect();
    observerRef.current = new IntersectionObserver(handler, options);

    if (ref.current) {
      observerRef.current.observe(ref.current);
    }

    return () => {
      if (timeoutRef.current !== null) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      observerRef.current?.disconnect();
    };
  }, [ref, options, delay]);
};
